import type { Collection, Watch, Enquiry, EnquiryStatus } from '@/types'
import type { SeoConfig } from '@shared/seo.config'

/**
 * ONLY bridge to the server. No component or hook fetches a URL directly —
 * everything network-related goes through a function exported here, per the
 * fullstack-scaffold rule (see server/, AGENT.md §1.4).
 *
 * Wire format is snake_case end to end (matches `types/index.ts` and the
 * `tf()` bilingual-field helper in `src/i18n`) — the server maps to/from its
 * own camelCase Drizzle columns at the boundary (server/lib/case.ts).
 */

export interface AppUser {
  id: string
  email: string
  name: string | null
  role?: string
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  const text = await res.text()
  const data = text ? JSON.parse(text) : null
  if (!res.ok) {
    const msg = (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string')
      ? data.error
      : `HTTP ${res.status}`
    throw new ApiError(msg, res.status)
  }
  return data as T
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  me: () => request<{ user: AppUser | null }>('/api/auth/me'),
  signUp: (email: string, password: string, name?: string) =>
    request<{ user: AppUser }>('/api/auth/signup', { method: 'POST', body: JSON.stringify({ email, password, name }) }),
  signIn: (email: string, password: string) =>
    request<{ user: AppUser }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  signOut: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
}

// ── Catalogue (public read-only) ─────────────────────────────────────────────

export const catalogApi = {
  collections: () => request<Collection[]>('/api/collections'),
  watches: () => request<Watch[]>('/api/watches'),
  watch: (id: string) => request<Watch>(`/api/watches/${id}`),
}

// ── Watches management (owner studio + public view counter) ──────────────────

export const watchesApi = {
  create: (draft: Partial<Watch>) =>
    request<Watch>('/api/watches', { method: 'POST', body: JSON.stringify(draft) }),
  update: (id: string, patch: Partial<Watch>) =>
    request<Watch>(`/api/watches/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string) =>
    request<{ ok: true }>(`/api/watches/${id}`, { method: 'DELETE' }),
  incrementViews: (id: string) =>
    request<Watch>(`/api/watches/${id}/view`, { method: 'POST' }),
}

// ── Enquiries (visitor submissions & studio inbox) ───────────────────────────

export const enquiriesApi = {
  all: () => request<Enquiry[]>('/api/enquiries'),
  send: (draft: Omit<Enquiry, 'id' | 'status' | 'created_at'>) =>
    request<Enquiry>('/api/enquiries', { method: 'POST', body: JSON.stringify(draft) }),
  setStatus: (id: string, status: EnquiryStatus) =>
    request<Enquiry>(`/api/enquiries/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  remove: (id: string) =>
    request<{ ok: true }>(`/api/enquiries/${id}`, { method: 'DELETE' }),
}

// ── SEO Settings ─────────────────────────────────────────────────────────────

export const seoApi = {
  get: () => request<SeoConfig>('/api/settings/seo'),
  save: (patch: Partial<SeoConfig>) =>
    request<SeoConfig>('/api/settings/seo', { method: 'PUT', body: JSON.stringify(patch) }),
}

// ── Dev / Seeding ────────────────────────────────────────────────────────────

export const devApi = {
  seed: () => request<{ ok: boolean; seeded: boolean }>('/api/dev/seed', { method: 'POST' }),
}
